import React, { useEffect, useState } from 'react';
import { Button, Drawer, Form, Select, Upload } from 'antd';
import type { UploadFile } from 'antd';
import { cargarFichas, estadoError, listarFerias, mensajeError } from '../../api/feriasApi';
import type { Feria, ResultadoCarga } from '../../api/feriasApi';
import { FctAviso } from '../../fct/react';
import { ResultadosCarga } from './ResultadosCarga';

export interface CargaFichasProps {
  abierto: boolean;
  onCerrar: () => void;
  /** Se llama tras una carga que creó al menos un caso, para que la bandeja se recargue. */
  onCargado: () => void;
}

/**
 * Carga de las fichas escaneadas de una feria (diseño §4 de fct-ferias): elegir una feria activa,
 * soltar o elegir los archivos, cargarlos de una vez y leer un resultado por archivo.
 */
export function CargaFichas({ abierto, onCerrar, onCargado }: CargaFichasProps) {
  const [ferias, setFerias] = useState<Feria[]>([]);
  const [feria, setFeria] = useState<number | undefined>();
  const [archivos, setArchivos] = useState<UploadFile[]>([]);
  const [cargando, setCargando] = useState(false);
  const [resultados, setResultados] = useState<ResultadoCarga[] | null>(null);
  const [error, setError] = useState<string | null>(null);

  // Cada vez que se abre: sin los resultados ni el error de la carga anterior, y con las ferias
  // activas; si solo hay una, queda elegida.
  useEffect(() => {
    if (!abierto) {
      return;
    }
    setResultados(null);
    setError(null);
    listarFerias(true)
      .then((activas) => {
        setFerias(activas);
        setFeria((actual) => actual ?? (activas.length === 1 ? activas[0].id : undefined));
      })
      .catch((e: unknown) => setError(mensajeError(e)));
  }, [abierto]);

  const cargar = async () => {
    if (feria === undefined) {
      return;
    }
    setCargando(true);
    setError(null);
    try {
      const respuesta = await cargarFichas(
        feria,
        archivos.map((a) => a.originFileObj as File),
      );
      setResultados(respuesta);
      setArchivos([]);
      if (respuesta.some((r) => r.idCaso !== null)) {
        onCargado();
      }
    } catch (e) {
      // La API admite hasta 100 MB por petición (D-FE-27); por encima responde 413 sin cuerpo.
      setError(
        estadoError(e) === 413
          ? 'Las fichas elegidas pasan de 100 MB juntas. Súbalas en tandas más pequeñas.'
          : mensajeError(e),
      );
    } finally {
      setCargando(false);
    }
  };

  const etiquetaCargar =
    archivos.length === 0 ? 'Subir fichas' : archivos.length === 1 ? 'Subir 1 ficha' : `Subir ${archivos.length} fichas`;

  return (
    <Drawer title="Subir fichas" open={abierto} onClose={onCerrar} width={520} destroyOnClose>
      <Form layout="vertical">
        <Form.Item label="Feria" required>
          <Select
            aria-label="Feria"
            placeholder="Elija la feria de las fichas"
            value={feria}
            onChange={setFeria}
            options={ferias.map((f) => ({ value: f.id, label: f.nombre }))}
            notFoundContent="No hay ferias activas."
          />
        </Form.Item>
        <Form.Item label="Fichas escaneadas (PDF de dos páginas, hasta 10 MB cada una)">
          <Upload.Dragger
            multiple
            accept=".pdf,application/pdf"
            fileList={archivos}
            beforeUpload={(archivo) => {
              // Se reúnen aquí y se envían juntos; nada se carga al soltarlos.
              setArchivos((lista) => [...lista, { uid: archivo.uid, name: archivo.name, originFileObj: archivo }]);
              setResultados(null);
              return false;
            }}
            onRemove={(archivo) => setArchivos((lista) => lista.filter((a) => a.uid !== archivo.uid))}
          >
            <p>Arrastre aquí los PDF o pulse para elegirlos.</p>
          </Upload.Dragger>
        </Form.Item>
        <Button type="primary" onClick={cargar} loading={cargando} disabled={feria === undefined || archivos.length === 0}>
          {etiquetaCargar}
        </Button>
      </Form>
      {error && (
        <FctAviso tipo="error" titulo="No se ha podido subir">
          {error}
        </FctAviso>
      )}
      {resultados && <ResultadosCarga resultados={resultados} />}
    </Drawer>
  );
}
